import { useState } from 'react'
import { Button, Form, Input, InputNumber, Switch, Tag, App } from 'antd'
import { PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leaveTypesApi } from '@/features/hr/api'
import type { LeaveType } from '@/shared/types/hr'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

export default function LeaveTypesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<LeaveType | null>(null)

  const canCreate = usePermission('leave.create')
  const canUpdate = usePermission('leave.update')

  const { data, isLoading } = useQuery({
    queryKey: ['leave-types'],
    queryFn: () => leaveTypesApi.list(),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ is_active: true, carry_forward: false, days_per_year: 0, max_accumulation: 0 })
    setDrawerOpen(true)
  }

  const openEdit = (record: LeaveType) => {
    setEditing(record)
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing ? leaveTypesApi.update(editing.id, values) : leaveTypesApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-types'] })
      message.success(editing ? 'Leave type updated' : 'Leave type created')
      setDrawerOpen(false)
    },
    onError: () => message.error('Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Code', dataIndex: 'leave_code', key: 'leave_code', width: 100 },
    { title: 'Name', dataIndex: 'leave_name', key: 'leave_name' },
    { title: 'Days/Year', dataIndex: 'days_per_year', key: 'days_per_year', width: 100 },
    {
      title: 'Carry Forward', dataIndex: 'carry_forward', key: 'carry_forward', width: 120,
      render: (v: boolean) => <Tag color={v ? 'blue' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    { title: 'Max Accumulation', dataIndex: 'max_accumulation', key: 'max_accumulation', width: 140 },
    {
      title: 'Status', dataIndex: 'is_active', key: 'is_active',
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, record: LeaveType) =>
        canUpdate ? <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button> : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Leave Types"
        subtitle="Manage leave categories"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Leave Type</Button>}
      />
      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer title={editing ? 'Edit Leave Type' : 'New Leave Type'} open={drawerOpen} onClose={() => setDrawerOpen(false)} onSubmit={onSubmit} loading={saveMutation.isPending} form={form}>
        <Form.Item name="leave_code" label="Leave Code" rules={[{ required: true }]}><Input maxLength={10} disabled={!!editing} /></Form.Item>
        <Form.Item name="leave_name" label="Leave Name" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="days_per_year" label="Days Per Year" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
        <Form.Item name="carry_forward" label="Carry Forward" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="max_accumulation" label="Max Accumulation"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
      </FormDrawer>
    </div>
  )
}
